<template>
    <div class="auth-wrapper auth-v1">
        <div class="auth-inner">
            <vue-element-loading :active="submiting" />
            <v-card class="auth-card">
                <!-- logo -->
                <v-card-title class="d-flex flex-column align-center justify-center py-7">
                    <brand-logo size="lg" />
                    <h2 class="text-2xl font-weight-semibold mt-4">
                        {{ $t('sign_up') }}
                    </h2>
                </v-card-title>

                <!-- title -->
                <v-card-text>
                    <p class="font-weight-semibold text--primary mb-2">
                        {{ $t('create_new_school_account') }}
                    </p>
                    <p class="mb-2">
                        {{ $t('please_fill_form_to_create_school_account') }}
                    </p>
                </v-card-text>

                <!-- register form -->
                <v-card-text v-if="step === 1">
                    <v-form ref="form" v-model="valid" lazy-validation>
                        <v-text-field
                            v-model="username"
                            outlined
                            :label="$t('full_school_name')"
                            placeholder="JohnDoe"
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>

                        <v-text-field
                            v-model="email"
                            outlined
                            :label="$t('Email')"
                            placeholder="john@example.com"
                            hide-details
                            required
                            :rules="emailRules"
                            class="mb-3"
                        ></v-text-field>

                        <v-text-field
                            v-model="password"
                            outlined
                            :type="isPasswordVisible ? 'text' : 'password'"
                            :label="$t('Password')"
                            required
                            :rules="passRules"
                            placeholder="············"
                            :append-icon="
                                isPasswordVisible
                                    ? icons.mdiEyeOffOutline
                                    : icons.mdiEyeOutline
                            "
                            hide-details
                            @click:append="
                                isPasswordVisible = !isPasswordVisible
                            "
                        ></v-text-field>

                        <v-text-field
                            v-model="confirmPassword"
                            outlined
                            class="mt-3"
                            :type="isPasswordVisible ? 'text' : 'password'"
                            label="Confirm password"
                            required
                            :rules="confirmRules"
                            placeholder="············"
                            hide-details
                        ></v-text-field>

                        <v-checkbox v-model="agree">
                            <template v-slot:label>
                                <div>
                                        {{ $t('i_agree_to') }}
                                    <v-tooltip location="bottom">
                                        <template v-slot:activator="{ props }">
                                            <a
                                                href="/terms"
                                                target="_blank"
                                                v-bind="props"
                                                @click.stop
                                            >
                                                {{ $t('Terms & Conditions') }}
                                            </a>
                                        </template>
                                    </v-tooltip>
                                </div>
                            </template>
                        </v-checkbox>

                        <v-card-text
                            class="d-flex align-center justify-center flex-wrap mt-2"
                        >
                            <span class="me-2"> {{ $t('need_help') }} </span>
                            <a href="#" @click="adminInfoDialog = true">
                                {{ $t('contact_us') }}
                            </a>
                        </v-card-text>

                        <VueSimpleRecaptcha
                            :sitekey="siteKey"
                            ref="recaptcha"
                            @callback="callback"
                            :invisible="true"
                        />
                        <v-btn
                            block
                            color="primary"
                            class="mt-6"
                            @click="register"
                            :disabled="!agree"
                        >
                            {{ $t('sign_up') }}
                        </v-btn>
                    </v-form>
                </v-card-text>

                <v-card-text v-else-if="step === 2">
                    <p class="font-weight-semibold">Check your email</p>
                    <p>We sent a verification link to {{ email }}. Open it, then continue.</p>
                    <v-alert v-if="verifyError" type="error" dense class="mb-3">{{ verifyError }}</v-alert>
                    <v-btn block color="primary" class="mt-2" :loading="submiting" @click="checkEmailVerified">I have verified</v-btn>
                    <v-btn block text class="mt-2" :loading="submiting" @click="resendEmail">Send the email again</v-btn>
                </v-card-text>

                <v-card-text v-else>
                    <p class="font-weight-semibold mb-3">School location</p>
                    <v-form ref="placeForm" @submit.prevent="savePlace">
                        <v-text-field v-model="telNumber" outlined dense label="Telephone" :rules="[must]" class="mb-2"></v-text-field>
                        <region-city-fields
                            :region-id.sync="regionId"
                            :city-id.sync="cityId"
                            :area.sync="area"
                            required
                        />
                        <v-text-field v-model="schoolAddress" outlined dense label="School address" :rules="[must]" class="mb-2"></v-text-field>
                        <v-alert v-if="verifyError" type="error" dense class="mb-3">{{ verifyError }}</v-alert>
                        <v-btn block color="primary" type="submit" :loading="submiting">Save and continue</v-btn>
                    </v-form>
                </v-card-text>

                <!-- create new account  -->
                <v-card-text
                    class="d-flex align-center justify-center flex-wrap mt-2"
                >
                    <span class="me-2"> {{ $t('already_have_account') }} </span>
                    <router-link :to="{ path: '/signin' }">
                        {{ $t('sign_in_instead') }}
                    </router-link>
                    <span class="mx-2">·</span>
                    <router-link to="/">{{ $t('back_to_website') }}</router-link>
                </v-card-text>
            </v-card>
            <v-row justify="center">
                <v-dialog
                    v-model="adminInfoDialog"
                    persistent
                    max-width="600px"
                >
                    <v-card>
                        <v-card-title>
                            <span class="text-h5">{{ $t('contact_us') }}</span>
                        </v-card-title>
                        <v-card-text>
                            <v-container>
                                <v-row>
                                    <v-col cols="12" sm="6">
                                        {{ $t('Email') }}:
                                        <a
                                            v-bind="{
                                                href:
                                                    'mailto:' + adminInfo.email,
                                            }"
                                        >
                                            {{ adminInfo.email }}
                                        </a>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        {{ $t('Phone Number') }}:
                                        <a
                                            v-bind="{
                                                href: 'tel:' + adminInfo.phone,
                                            }"
                                        >
                                            {{ adminInfo.phone }}
                                        </a>
                                    </v-col>
                                    <v-col cols="12">
                                        {{ $t('Address') }}:
                                        <span>{{ adminInfo.address }}</span>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                                color="blue darken-1"
                                text
                                @click="adminInfoDialog = false"
                            >
                                {{ $t('close') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-row>
        </div>

    </div>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import {
    mdiFacebook,
    mdiTwitter,
    mdiGithub,
    mdiGoogle,
    mdiEyeOutline,
    mdiEyeOffOutline,
} from "@mdi/js";
import { ref } from "@vue/composition-api";
import { Keys } from "/src/config.js";
import VueElementLoading from "vue-element-loading";
import AuthService from "@/services/AuthService";
import VueSimpleRecaptcha from "vue-simple-recaptcha";
import BrandLogo from "@/components/BrandLogo.vue";
import RegionCityFields from "@/components/RegionCityFields.vue";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";

export default {
    components: {
        VueElementLoading,
        VueSimpleRecaptcha,
        BrandLogo,
        RegionCityFields,
    },
    setup() {
        const isPasswordVisible = ref(false);
        const username = ref("");
        const email = ref("");
        const password = ref("");
        const confirmPassword = ref("");
        const socialLink = [
            {
                icon: mdiFacebook,
                color: "#4267b2",
                colorInDark: "#4267b2",
            },
            {
                icon: mdiTwitter,
                color: "#1da1f2",
                colorInDark: "#1da1f2",
            },
            {
                icon: mdiGithub,
                color: "#272727",
                colorInDark: "#fff",
            },
            {
                icon: mdiGoogle,
                color: "#db4437",
                colorInDark: "#db4437",
            },
        ];

        return {
            systemName: Keys.VUE_APP_SYSTEM_NAME,
            siteKey: Keys.VUE_APP_RECAPTCHA_SITE_KEY,
            isPasswordVisible,
            username,
            email,
            password,
            confirmPassword,
            socialLink,

            icons: {
                mdiEyeOutline,
                mdiEyeOffOutline,
            },
        };
    },
    data() {
        return {
            systemName: Keys.VUE_APP_SYSTEM_NAME,
            valid: true,
            submiting: false,
            emailRules: [
                (v) => !!v || this.$t("E-mail is required"),
                (v) => /.+@.+\..+/.test(v) || this.$t("E-mail must be valid"),
            ],
            passRules: [(v) => !!v || this.$t("Password is required")],
            confirmRules: [
                (v) => !!v || "Confirm your password",
                (v) => v === this.password || "Passwords do not match",
            ],
            userNameRules: [(v) => !!v || this.$t("username_required")],
            agree: false,
            step: 1,
            verifyError: "",
            telNumber: "",
            regionId: null,
            cityId: null,
            area: "",
            schoolAddress: "",
            adminInfo: {
                email: "",
                phone: "",
                address: "",
            },
            adminInfoDialog: false,
        };
    },
    mounted() {
        this.loadAdminInfo();
    },
    methods: {
        loadAdminInfo() {
            this.submiting = true;
            axios
                .get(`/users/admin-info`)
                .then((response) => {
                    console.log(response.data.admin_info.email);
                    this.adminInfo.email = response.data.admin_info.email;
                    this.adminInfo.phone = response.data.admin_info.phone;
                    this.adminInfo.address = response.data.admin_info.address;
                })
                .catch((error) => {
                    this.$notify({
                        title: "Error",
                        text: "Error while retrieving buses",
                        type: "error",
                    });
                    console.log(error);
                })
                .then(() => {
                    this.submiting = false;
                });
        },
        validate() {
            return this.$refs.form.validate();
        },
        async callback(token) {
            const payload = {
                email: this.email,
                name: this.username,
                password: this.password,
                recaptcha: token,
                notify: this.$notify,
            };
            this.error = null;
            try {
                this.submiting = true;
                const user = await AuthService.register(payload);
                console.log(user);
                this.submiting = false;
                if (user) {
                    this.step = 3;
                }
            } catch (error) {
                console.log(error);
                this.submiting = false;
                //this.error = getError(error);
            }
        },
        async register() {
            if (!this.validate()) return;
            this.$refs.recaptcha.execute();
        },
        must(v) {
            return !!v || "Required";
        },
        async checkEmailVerified() {
            this.verifyError = "";
            this.submiting = true;
            try {
                const user = firebase.auth().currentUser;
                if (!user) {
                    this.verifyError = "Sign in again, then open the email link.";
                    return;
                }
                await user.reload();
                if (!user.emailVerified) {
                    this.verifyError = "The email is not verified yet. Open the link we sent, then try again.";
                    return;
                }
                this.step = 3;
            } finally {
                this.submiting = false;
            }
        },
        async resendEmail() {
            this.verifyError = "";
            this.submiting = true;
            try {
                const user = firebase.auth().currentUser;
                if (user) await user.sendEmailVerification();
            } finally {
                this.submiting = false;
            }
        },
        async savePlace() {
            if (this.$refs.placeForm && !this.$refs.placeForm.validate()) return;
            this.verifyError = "";
            this.submiting = true;
            try {
                await axios.post("/users/update-profile", {
                    tel_number: this.telNumber,
                    region_id: this.regionId,
                    city_id: this.cityId,
                    area: this.area,
                    address: this.schoolAddress,
                });
                this.$router.push("/school-dashboard");
            } catch (error) {
                this.verifyError = (error.response && error.response.data && error.response.data.message) || error.message;
            } finally {
                this.submiting = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@/plugins/vuetify/default-preset/preset/pages/auth.scss';

.language-selector-wrapper {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 10;
}

.language-selector {
  max-width: 56px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 999px;
  box-shadow: 0 2px 8px rgba(0, 40, 85, 0.08);

  ::v-deep .v-input__control,
  ::v-deep .v-input__slot {
    min-height: 36px !important;
  }

  ::v-deep .v-input__slot {
    border: 0 !important;
    border-radius: 999px !important;
    padding: 0 10px !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  ::v-deep .v-select__selections {
    padding: 0 !important;
  }
}

.theme--dark {
  .language-selector {
    background: rgba(30, 30, 30, 0.95);

    ::v-deep .v-input__slot:hover {
      border-color: rgba(var(--v-primary-base), 0.5) !important;
    }
  }
}

.selected-language {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 4px 0;
}

.flag-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 19px;
  border-radius: 3px;
  overflow: hidden;
}

.theme--dark .flag-container {
  background: rgba(255, 255, 255, 0.05);

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
}

.flag-img {
  width: 28px;
  height: 19px;
  border-radius: 3px;
  object-fit: cover;
  display: block;
}

.language-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  width: 100%;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: rgba(var(--v-primary-base), 0.08);

    .flag-container {
      transform: scale(1.05);
    }

    .language-name {
      color: rgb(var(--v-primary-base));
      transform: translateX(2px);
    }
  }
}

.language-name {
  margin-left: 12px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

::v-deep .language-menu {
  border-radius: 12px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  margin-top: 8px !important;
  overflow: hidden;

  .v-list {
    padding: 4px !important;
    background: rgba(255, 255, 255, 0.98) !important;
    backdrop-filter: blur(10px);
  }

  .v-list-item {
    border-radius: 8px !important;
    margin: 2px 0 !important;
    min-height: 44px !important;

    &::before {
      border-radius: 8px !important;
    }
  }
}

.theme--dark ::v-deep .language-menu .v-list {
  background: rgba(30, 30, 30, 0.98) !important;
}

// Smooth animations
.slide-y-transition-enter-active,
.slide-y-transition-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-y-transition-enter,
.slide-y-transition-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
